import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, CountryCollectionQuery, CountryItem, OssVatRateBO, OssVatRateRepositoryBase } from '@skylab-shared/business';
import { EventBusBase, IValueConverter, IdentifiableValueConverter, ObservableCollection, RepositorySaveResponse, ViolationSeverity } from '@skylab-shared/core';
import { OssVatRateModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, head } from '@skylab-shared/utils';

import { OssVatRateRepository } from '@skylab-angular-shared/business';
import { CommandBus, EventBus, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, DialogState, FocusByDirective, FormContainerComponent, NumericValueAccessorDirective, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface OssVatRateDialogData extends DictionaryDialogData {
    countryId?: number;
}

export interface OssVatRateDialogResult {
    response: {
        id: number;
        version: string;
    };
    default: {
        previous: boolean;
        current: boolean;
    };
    countryId: number;
}

interface OssVatRateDialogViewModel extends DictionaryDialogViewModelBase<OssVatRateBO> {
    countries: CountryItem[];
    countryValueConverter: IdentifiableValueConverter;
    rateConverter: IValueConverter<number, number>;
    rate: {
        value: number | null;
    };
    validationSource: { validationResult: ObservableCollection<any> };
}
@Component({
    selector: 'skl-oss-vat-rate-dialog',
    templateUrl: './oss-vat-rate-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, BooleanValueAccessorDirective, FormsModule, ValidateDirective, TextValueAccessorDirective, FocusByDirective, SelectValueAccessorDirective, NumericValueAccessorDirective, ButtonComponent, TranslatePipe]
})
export class OssVatRateDialogComponent extends DictionaryDialogComponentBase<OssVatRateModel, OssVatRateBO, OssVatRateRepositoryBase, OssVatRateDialogViewModel, OssVatRateDialogData> {
    private previousDefaultValue!: boolean;
    private rate!: number | null;
    public readonly customValidationSun = 'oss-vat-rate-dialog-form-validation-sun';

    public constructor(
        @Inject(OssVatRateRepository) ossVatRateRepositoryBase: OssVatRateRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        toastService: ToastService,
        commandBus: CommandBus,
        dialogService: DialogInsService
    ) {
        super(ossVatRateRepositoryBase, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            this.localContext.preload(CountryCollectionQuery),
            this.localContext.preload(CompanyParametersQuery)
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        const self = this;
        this.rate = this.viewModel.isFormInEditMode ? this.viewModel.bo.model.rate : null;

        this.previousDefaultValue = this.viewModel.bo.model.default;
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery!);
        const systemCountryId = companyParameters?.systemCountryId;
        this.viewModel.countries = this.localContext.filterPreloaded(CountryCollectionQuery, { euMember: true }).filter(elem => elem.id !== systemCountryId);
        this.viewModel.countryValueConverter = new IdentifiableValueConverter(this.viewModel.countries);
        this.viewModel.rateConverter = {
            convert: value => Calc.round(value * 100, 6, MidpointRounding.AwayFromZero),
            convertBack: value => Calc.round(value / 100, 6, MidpointRounding.AwayFromZero)
        };
        this.viewModel.bo.model.countryId ||= (this.data.countryId || head(this.viewModel.countries).id);

        if (!this.viewModel.isFormInEditMode) {
            this.viewModel.bo.model.active = true;
        }

        this.viewModel.validationSource = { validationResult: new ObservableCollection<any>() };

        this.viewModel.rate = {
            get value() {
                return self.rate;
            },
            set value(val) {
                self.rate = val;
                if (self.rate != null) {
                    self.viewModel.bo.model.rate = self.rate;
                }
                self.updateFormValidation({ rate: self.rate });
            }
        };
    }

    protected override async initViewValidation(): Promise<void> {
        this.viewValidation = this.viewValidationService.create([this.viewModel.bo, this.viewModel.validationSource], this.viewModel.showValidations);
    }

    protected override onSaveSuccess(response?: RepositorySaveResponse): void {
        if (response?.object) {
            const { id, version } = response.object;
            this.dialogService.close<OssVatRateDialogResult>(
                {
                    state: DialogState.Confirm,
                    data: {
                        response: { id, version },
                        default: {
                            current: this.viewModel.bo.model.default,
                            previous: this.previousDefaultValue
                        },
                        countryId: this.viewModel.bo.model.countryId
                    }
                });
        }
    }

    private updateFormValidation(fields: { rate: number | null }): void {
        this.viewModel.validationSource.validationResult.clear();

        if (fields.rate == null) {
            const error = {
                source: {
                    sun: this.customValidationSun
                },
                propertyName: 'rate',
                ruleViolation: {
                    violation: {
                        severity: ViolationSeverity.Error,
                        message: 'scenes.configuration.shared.oss-vat-rate.dialog.form.rate.validation.error'
                    }
                }
            };
            this.viewModel.validationSource.validationResult.push(error);
        }
    }

    public override async canSaveAsync(): Promise<boolean> {
        this.updateFormValidation({ rate: this.rate });

        return await super.canSaveAsync() && this.viewModel.validationSource.validationResult?.length === 0;
    }
}
